import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import ComicCard from '@/components/feature/ComicCard';
import { getComicById, getRelatedComics, comics } from '@/mocks/comics';

export default function ComicDetailPage() {
  const { id } = useParams<{ id: string }>();
  const comic = getComicById(Number(id));
  const [favorited, setFavorited] = useState(false);

  if (!comic) {
    return (
      <Layout>
        <div className="mx-auto max-w-3xl px-4 py-24 text-center">
          <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          <h1 className="mt-4 font-display text-2xl text-foreground-900">没有找到这部漫画</h1>
          <p className="mt-2 text-sm text-foreground-600">作品可能已下架，去全部漫画看看吧。</p>
          <Link to="/comics" className="mt-6 inline-block rounded-full bg-primary-500 px-6 py-3 text-sm text-background-50 hover:bg-primary-600">
            返回全部漫画
          </Link>
        </div>
      </Layout>
    );
  }

  const related = getRelatedComics(comic);
  const lastChapter = comic.chapters[0];

  return (
    <Layout>
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-12">
        <nav aria-label="面包屑" className="mb-6 text-sm text-foreground-500">
          <ol className="flex flex-wrap items-center gap-1.5">
            <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li><Link to="/comics" className="hover:text-primary-600">全部漫画</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li><Link to={`/comics?category=${encodeURIComponent(comic.category)}`} className="hover:text-primary-600">{comic.category}</Link></li>
            <li><i className="ri-arrow-right-s-line"></i></li>
            <li><span className="text-foreground-700">{comic.title}</span></li>
          </ol>
        </nav>

        <div className="overflow-hidden rounded-xl border border-background-200 bg-background-100">
          <div className="flex flex-col gap-6 p-5 md:flex-row md:p-8">
            <div className="relative mx-auto w-full max-w-[220px] shrink-0 md:mx-0">
              <div className="aspect-[3/4] w-full overflow-hidden rounded-lg">
                <img src={comic.cover} alt={`${comic.title}漫画封面`} title={`${comic.title} · ${comic.category}漫画`} className="h-full w-full object-cover object-top" />
              </div>
              <span className={`absolute left-2 top-2 rounded-md px-2 py-0.5 text-xs font-medium text-background-50 ${comic.status === '已完结' ? 'bg-accent-500' : 'bg-primary-500'}`}>
                {comic.status}
              </span>
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="font-display text-2xl text-foreground-900 md:text-3xl">{comic.title}</h1>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {comic.tags.map((t) => (
                  <span key={t} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">{t}</span>
                ))}
                <span className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-600">人气 {comic.views}</span>
              </div>

              <dl className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-lg bg-background-50 p-3">
                  <dt className="text-xs text-foreground-500">评分</dt>
                  <dd className="mt-1 flex items-center gap-1 font-semibold text-foreground-900">
                    <i className="ri-star-fill text-secondary-500"></i>{comic.rating}
                  </dd>
                </div>
                <div className="rounded-lg bg-background-50 p-3">
                  <dt className="text-xs text-foreground-500">作者</dt>
                  <dd className="mt-1 truncate font-semibold text-foreground-900">{comic.author}</dd>
                </div>
                <div className="rounded-lg bg-background-50 p-3">
                  <dt className="text-xs text-foreground-500">分类</dt>
                  <dd className="mt-1 font-semibold text-foreground-900">{comic.category}</dd>
                </div>
                <div className="rounded-lg bg-background-50 p-3">
                  <dt className="text-xs text-foreground-500">收藏</dt>
                  <dd className="mt-1 font-semibold text-foreground-900">{comic.favorite}</dd>
                </div>
              </dl>

              <h2 className="mt-6 text-sm font-semibold text-foreground-900">作品简介</h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{comic.intro}</p>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to={lastChapter ? `/comic/${comic.id}/read` : '/comics'}
                  className="rounded-full bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 hover:bg-primary-600 whitespace-nowrap"
                >
                  <i className="ri-play-circle-line mr-1"></i>开始阅读 · {lastChapter.title}
                </Link>
                <button
                  type="button"
                  onClick={() => setFavorited((v) => !v)}
                  className={`rounded-full px-6 py-3 text-sm font-medium whitespace-nowrap ${favorited ? 'bg-primary-500 text-background-50' : 'bg-background-50 text-foreground-700 hover:bg-background-200'}`}
                >
                  <i className={`${favorited ? 'ri-heart-fill' : 'ri-heart-line'} mr-1`}></i>
                  {favorited ? '已收藏' : '收藏'}
                </button>
                <span className="text-sm text-foreground-500">更新于 {comic.lastUpdated}</span>
              </div>
            </div>
          </div>

          <div className="border-t border-background-200 p-5 md:p-8">
            <h2 className="mb-4 font-display text-lg text-foreground-900">章节目录</h2>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {comic.chapters.map((ch) => (
                <Link
                  key={ch.id}
                  to={`/comic/${comic.id}/read`}
                  className="group flex items-center justify-between gap-3 rounded-lg bg-background-50 px-4 py-3 hover:bg-secondary-50"
                >
                  <span className="truncate text-sm text-foreground-700 group-hover:text-secondary-900">{ch.title}</span>
                  <span className="flex shrink-0 items-center gap-2 text-xs text-foreground-400">
                    {ch.views} <i className="ri-arrow-right-s-line"></i>
                  </span>
                </Link>
              ))}
            </div>
            <Link to="/comics" className="mt-4 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700">
              查看全部 {comic.totalChapters} 话 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <section className="mt-12">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <h2 className="font-display text-xl text-foreground-900 md:text-2xl">相关推荐</h2>
              <p className="mt-1 text-sm text-foreground-600">喜欢这部漫画的朋友也在追这些作品。</p>
            </div>
            <Link to="/comics" className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 whitespace-nowrap">
              浏览更多 <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
            {related.map((c) => (
              <ComicCard key={c.id} comic={c} />
            ))}
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: comic.title,
              description: comic.intro,
              image: comic.cover,
              keywords: `${comic.title},${comic.category}漫画,${comic.tags.join(',')}`,
              author: { '@type': 'Organization', name: comic.author },
              dateModified: comic.lastUpdated,
            }),
          }}
        />
      </div>
    </Layout>
  );
}